// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Document Manager panel - upload new versions, view version history,
 * delete versions or the entire document.
 * Version comparison moved to inline controls in pipeline cards.
 */
import { useCallback, useEffect, useState, type FC } from 'react'
import { X, Upload, Loader2, CheckCircle2, FileText, Trash2, Download } from 'lucide-react'
import { Progress } from '@/components/ui'

import type { DocumentVersion } from '@/subsystems/documents'
import type { NodeApiClient } from '@/app-shell/services'
import { formatFileSize, formatDate } from '../utils'

interface DocumentDetail {
  documentId: string
  name: string
  latestVersion: number
  versions: DocumentVersion[]
}

interface VersionPanelProps {
  documentId: string | null
  basePath: string | null
  apiClient: NodeApiClient
  onClose: () => void
  onVersionUploaded: () => void
  onDeleteDocument: (documentId: string) => void
  allowChanges?: boolean
}

export const VersionPanel: FC<VersionPanelProps> = ({
  documentId,
  basePath,
  apiClient,
  onClose,
  onVersionUploaded,
  onDeleteDocument,
  allowChanges = true,
}) => {
  const [document, setDocument] = useState<DocumentDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [uploadSkippedMessage, setUploadSkippedMessage] = useState<string | null>(null)
  const [dragActive, setDragActive] = useState(false)
  const [deletingVersion, setDeletingVersion] = useState<number | null>(null)

  // Fetch document details when panel opens
  useEffect(() => {
    if (!documentId || !basePath) {
      setDocument(null)
      return
    }

    const fetchDocument = async () => {
      setLoading(true)
      setUploadSuccess(false)
      try {
        const { data } = await apiClient.get<DocumentDetail>(
          `${basePath}/documents/${documentId}`
        )
        setDocument(data)
      } catch {
        setDocument(null)
      } finally {
        setLoading(false)
      }
    }

    void fetchDocument()
  }, [apiClient, basePath, documentId])

  // Delete a specific version
  const deleteVersion = useCallback(async (version: number) => {
    if (!documentId || !basePath || !document) return
    if (document.versions.length <= 1) return

    setDeletingVersion(version)

    try {
      await apiClient.delete(`${basePath}/documents/${documentId}/versions/${version}`)

      // Refresh document details
      const { data } = await apiClient.get<DocumentDetail>(
        `${basePath}/documents/${documentId}`
      )
      setDocument(data)
      onVersionUploaded()
    } catch {
      // Error handled silently
    } finally {
      setDeletingVersion(null)
    }
  }, [apiClient, basePath, documentId, document, onVersionUploaded])

  // Download a specific version via presigned URL
  const downloadVersion = useCallback(async (version: number) => {
    if (!documentId || !basePath) return
    try {
      const wsBasePath = basePath.replace(/\/contract-review$/, '')
      const { data } = await apiClient.get<{ url: string }>(
        `${wsBasePath}/documents/${documentId}/view-url?version=${version}`
      )
      window.open(data.url, '_blank')
    } catch {
      // Error handled silently
    }
  }, [apiClient, basePath, documentId])

  const uploadFile = useCallback(async (file: File) => {
    if (!documentId || !basePath) return

    setUploading(true)
    setUploadProgress(0)
    setUploadSuccess(false)
    setUploadSkippedMessage(null)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const axios = apiClient.getAxiosInstance()
      const response = await axios.post<{ skipped?: boolean; message?: string }>(
        `${basePath}/documents/${documentId}/versions`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const progress = progressEvent.total
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 50
            setUploadProgress(progress)
          },
        }
      )

      if (response.data.skipped) {
        setUploadSkippedMessage(response.data.message ?? 'File content unchanged, not uploaded')
      } else {
        setUploadSuccess(true)
        onVersionUploaded()
      }

      // Refresh document details
      const { data } = await apiClient.get<DocumentDetail>(
        `${basePath}/documents/${documentId}`
      )
      setDocument(data)
    } catch {
      // Error handling
    } finally {
      setUploading(false)
    }
  }, [apiClient, basePath, documentId, onVersionUploaded])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      void uploadFile(file)
    }
    e.target.value = ''
  }, [uploadFile])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const file = e.dataTransfer.files?.[0]
    if (file) {
      void uploadFile(file)
    }
  }, [uploadFile])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
  }, [])

  const handleDeleteDocument = () => {
    if (documentId) {
      onDeleteDocument(documentId)
    }
  }

  const isOpen = documentId !== null
  const latestDocumentVersion = document?.versions.find(
    (version) => version.version === document.latestVersion,
  )

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[1000] bg-slate-900/40 transition-opacity duration-200 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        data-testid="cr-version-panel"
        className={`fixed inset-y-0 right-0 z-[1010] flex w-[440px] max-w-full transform flex-col bg-card shadow-2xl transition-transform duration-200 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b px-6 py-5">
          <div className="flex items-center gap-3">
            <span className="flex size-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
              <FileText className="h-4 w-4" />
            </span>
            <h2 data-testid="cr-version-panel-heading" className="text-lg font-semibold text-foreground">
              Document Manager
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="cr-version-close"
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Close Document Manager"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : document ? (
            <>
              {/* Document name */}
              <div className="space-y-1">
                <p className="text-base font-semibold leading-snug text-foreground">{document.name}</p>
                <p className="text-sm text-muted-foreground">
                  {document.versions.length} {document.versions.length === 1 ? 'version' : 'versions'}
                  {latestDocumentVersion?.createdAt
                    ? ` · Updated ${formatDate(latestDocumentVersion.createdAt)}`
                    : ''}
                </p>
              </div>

              {/* Upload dropzone */}
              {allowChanges ? <div
                className={`rounded-xl border border-dashed px-6 py-6 transition-colors ${
                  dragActive
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-300 bg-slate-50/40 hover:border-slate-400 hover:bg-slate-50'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
              >
                {uploading ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      <span className="text-sm text-foreground">Uploading...</span>
                    </div>
                    <Progress value={uploadProgress} className="h-1.5" />
                  </div>
                ) : uploadSkippedMessage ? (
                  <div className="flex items-center justify-center gap-2 text-amber-600">
                    <FileText className="h-5 w-5" />
                    <span className="text-sm font-medium">{uploadSkippedMessage}</span>
                  </div>
                ) : uploadSuccess ? (
                  <div className="flex items-center justify-center gap-2 text-emerald-600">
                    <CheckCircle2 className="h-5 w-5" />
                    <span className="text-sm font-medium">Version uploaded</span>
                  </div>
                ) : (
                  <label className="flex cursor-pointer flex-col items-center gap-3">
                    <span className="flex size-10 items-center justify-center rounded-lg border bg-white shadow-sm">
                      <Upload className={`h-5 w-5 ${dragActive ? 'text-blue-600' : 'text-slate-600'}`} />
                    </span>
                    <span className="text-center text-sm text-slate-600">
                      <strong className="font-semibold text-slate-900">Click to upload</strong>
                      {dragActive ? ' or drop file here' : ' or drag a new version'}
                    </span>
                    <span className="text-xs text-slate-400">PDF, DOCX, TXT · up to 25 MB</span>
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.doc,.docx,.txt"
                      onChange={handleFileSelect}
                    />
                  </label>
                )}
              </div> : (
                <p className="rounded-xl border bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  Versions are managed by this workspace&apos;s Vault connector.
                </p>
              )}

              {/* Version list */}
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Version history
                </p>
                <div className="space-y-2">
                  {[...document.versions]
                    .sort((a, b) => b.version - a.version)
                    .map((version) => (
                      <div
                        key={version.version}
                        data-testid={`cr-version-item-${version.version}`}
                        className={`flex items-center gap-3 rounded-xl border p-3 ${
                          version.version === document.latestVersion
                            ? 'border-blue-200 bg-blue-50/30'
                            : 'border-slate-200 bg-white'
                        }`}
                      >
                        <span className={`flex size-9 shrink-0 items-center justify-center rounded-lg ${
                          version.version === document.latestVersion
                            ? 'bg-blue-50 text-blue-600'
                            : 'bg-slate-100 text-slate-500'
                        }`}>
                          <FileText className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-foreground">Version {version.version}</span>
                            {version.version === document.latestVersion && (
                              <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-600">
                                <span className="size-1.5 rounded-full bg-blue-500" />
                                Current
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {formatFileSize(version.sizeBytes)} · {formatDate(version.createdAt)}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => void downloadVersion(version.version)}
                            className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                            title="Download version"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                          {/* Delete button - only show if more than 1 version */}
                          {allowChanges && document.versions.length > 1 && (
                            <button
                              type="button"
                              onClick={() => void deleteVersion(version.version)}
                              disabled={deletingVersion === version.version}
                              className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                              title="Delete version"
                            >
                              {deletingVersion === version.version ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>

            </>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-12">
              Document not found
            </p>
          )}
        </div>
        {allowChanges && document && !loading && (
          <div className="shrink-0 border-t bg-white px-6 py-5">
            <button
              type="button"
              onClick={handleDeleteDocument}
              data-testid="cr-version-delete-all"
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-200 px-4 py-3 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
              Delete All {document.versions.length > 1 ? `${document.versions.length} ` : ''}Versions
            </button>
            <p className="mt-2 text-center text-xs text-muted-foreground">This cannot be undone</p>
          </div>
        )}
      </div>
    </>
  )
}
